/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, {
    IComponentProps,
    IGridSettings,
    IImageSettings,
    Image,
    isChannelTypeB2B,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartLine, ProductCatalog, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl, ObjectExtensions, validateCatalogId, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import * as React from 'react';

import { PriceComponent, CartLinePriceEditor, ICartLinePriceEditorResources } from '@msdyn365-commerce/components';

//----------------------------------------------------------
// @CUSTOM: Backorder imports
//----------------------------------------------------------
import { ReleasedProductType } from '@msdyn365-commerce/retail-proxy';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: T&E imports
//----------------------------------------------------------
import { getCatalogId } from '@msdyn365-commerce/core';
import { ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import { convertProductAttributes } from '../../../../utilities/data-attribute-parser';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit custom imports
//----------------------------------------------------------
import { attrNames } from '../../../../utilities/global-constants';
import { convertCartAttributes } from '../../../../utilities/data-attribute-parser';
//----------------------------------------------------------

// @CUSTOM: custom analytics imports
import { publish } from '../../../../utilities/analytics/analytics-dispatcher';
import { SingleProductEvent } from '../../../../utilities/analytics/analytics-events';

//----------------------------------------------------------
// @CUSTOM: SKU display import
//----------------------------------------------------------
import { clsHelper } from '../../../../utilities/class-name-helper';
import { labelResources } from '../../../../utilities/global-constants';
//----------------------------------------------------------

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
export interface ICartlineResourceString {
    /**
     * Display string for discount label.
     */
    discountStringText: string;

    /**
     * String for size key.
     */
    sizeString: string;

    /**
     * String for color key.
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension.
     */
    configString: string;

    /**
     * String for style key.
     */
    styleString: string;

    /**
     * String for amount key.
     */
    amountString?: string;

    /**
     * Display string for quantity label.
     */
    quantityDisplayString: string;

    /**
     * Display string for quantity label.
     */
    inputQuantityAriaLabel: string;

    /**
     * Aria label for the decrement button in quantity component.
     */
    decrementButtonAriaLabel?: string;

    /**
     * Aria label for the increment button in quantity component.
     */
    incrementButtonAriaLabel?: string;

    /**
     * Original text screen reader.
     */
    originalPriceText: string;

    /**
     * Current text screen reader.
     */
    currentPriceText: string;

    /**
     * Shipping Charges Text.
     */
    shippingChargesText: string;

    priceEditorResources?: ICartLinePriceEditorResources;

    /**
     * Sales agreement price prompt text.
     */
    salesAgreementPricePrompt?: string;

    /**
     * Shipping text.
     */
    shippingText?: string;
}

/**
 * The data about the cart line and products.
 */
export interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
    catalogs?: ProductCatalog[];
}

export type CartLineDisplayMode = 'DEFAULT' | 'COMPACT';

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /**
     * The flag to change the quantity component from interactivity to static.
     */
    disableQuantityInteractivity?: boolean;

    /**
     * The primary image url.
     */
    primaryImageUrl?: string;

    /**
     * The product url.
     */
    productUrl?: string;

    /**
     * GridSettings for the product image in cartLine.
     */
    gridSettings: IGridSettings;

    /**
     * ImageSettings for the product image in cartLine.
     */
    imageSettings: IImageSettings;

    /**
     * Boolean flag to indicate if the item is out of stock.
     */
    isOutOfStock?: boolean;

    /**
     * Flag to make quantity section editable.
     */
    isQuantityEditable?: boolean;

    /**
     * Max quantity for line item.
     */
    maxQuantity?: number;

    /**
     * Current quantity for line item.
     */
    currentQuantity?: number;

    /**
     * Resource string for the component.
     */
    resources: ICartlineResourceString;

    /**
     * SalesLine flag.
     */
    isSalesLine?: boolean;

    /**
     * Backorder availability.
     */
    backorderAvailability: IProductInventoryInformation[];

    /**
     * Error message to show in place of quantity.
     */
    errorMessage?: string;

    /**
     * Display mode to use.
     */
    displayMode?: CartLineDisplayMode;

    /**
     * Inventory information label.
     */
    inventoryInformationLabel?: string;

    /**
     * Inventory information class name.
     */
    inventoryLabelClassName?: string;

    /**
     * Flag to show/hide shipping charges for line items.
     */
    showShippingChargesForLineItems?: boolean;

    /**
     * Boolean flag to indicate if cart state status is ready.
     */
    isCartStateReady?: boolean;

    /**
     * Chanel Delivery Option configuration is from api.
     */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;

    /**
     * The cart line index.
     */
    lineIndex?: number;

    /**
     * The cart line delivery location.
     */
    deliveryLocation?: string;

    /**
     * Quantity onChange callback.
     */
    quantityOnChange?(cartLine: CartLine, newQuantity: number, lineIndex?: number): void;
    priceCurrency?: string;
}

interface IDimensionStrings {
    /**
     * String for size key.
     */
    sizeString: string;

    /**
     * String for color key.
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension.
     */
    configString: string;

    /**
     * String for style key.
     */
    styleString: string;

    /**
     * String for amount key.
     */
    amountString?: string;
}

//------------------------------------------------------
// @CUSTOM: Class name utility for customizations that
// want to use the same base class
//------------------------------------------------------
const BASE_CLASS = 'msc-cart-line';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//------------------------------------------------------

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        if (!Product || !Product.Dimensions) {
            return [];
        }

        return Product.Dimensions.map(productDimension => {
            if (productDimension.DimensionTypeValue === 1) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {DimensionStrings.colorString}
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 2) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>
                            {DimensionStrings.configString}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 3) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {DimensionStrings.sizeString}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 4) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {Product.IsGiftCard ? DimensionStrings.amountString : DimensionStrings.styleString}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || props.data.cartLine.DiscountLines.length === 0) {
            return null;
        }

        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId || index} className='msc-cart-line-item-product-discount'>
                    <span className='msc-cart-line__product-savings-label'>
                        {discountLine.OfferName ? discountLine.OfferName : ''}:{` ${props.resources.discountStringText || 'Discount'} `}
                    </span>
                    <span className='msc-cart-line__promo-codes'>
                        <PriceComponent
                            data={
                                props.isSalesLine
                                    ? {
                                        price: {
                                            CustomerContextualPrice: (props.data.cartLine as SalesLine).PeriodicDiscount
                                        }
                                    }
                                    : {
                                        price: {
                                            CustomerContextualPrice: props.data.cartLine.DiscountAmount,
                                            BasePrice: props.data.cartLine.DiscountAmount
                                        }
                                    }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='msc-cart-line__discount-value'
                            currencyCode={props.priceCurrency}
                        />
                    </span>
                    <span>{` (${discountLine.Percentage !== undefined ? discountLine.Percentage : ''}%)`}</span>
                </div>
            );
        });
    },
    renderInventoryLabel: (props: ICartLineProps) => {
        // @CUSTOM: we do not want to show inventory status with backorders enabled
        return null;

        // if (!props.inventoryInformationLabel) {
        //     return null;
        // }
        // const inventoryCssName = props.inventoryLabelClassName
        //     ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
        //     : 'msc-cart-line__product-inventory-label';
        // return <span className={inventoryCssName}>{props.inventoryInformationLabel}</span>;
    },

    /**
     * Gets the react node for product unit of measure display.
     * @param  props - ICartLineProps props.
     * @returns The node representing markup for unit of measure component.
     */
    renderUnitOfMeasure: (props: ICartLineProps) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
        if (
            props.context.app.config &&
            (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none')
        ) {
            return undefined;
        }

        const product = props.data.product;
        if (!product || !product.DefaultUnitOfMeasure) {
            return undefined;
        }

        return (
            <div className='msc-cartline__product-unit-of-measure'>
                <span>{product.DefaultUnitOfMeasure}</span>
            </div>
        );
    },
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!(
                cartline.DeliveryMode &&
                channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
            );
        } else {
            hasShippingMethod = !!(cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode);
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines || []).filter(chargeLine => chargeLine.IsShipping);

        if (shippingChargeLines.length === 0) {
            return undefined;
        }

        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount || 0);
        }, 0);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText='Free'
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
                currencyCode={props.priceCurrency}
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines?.filter(chargeline => !chargeline.IsShipping);

        return (
            (otherCharges &&
                otherCharges.map((otherCharge, key) => {
                    return otherCharge && otherCharge.CalculatedAmount ? (
                        <div className='msc-cart-line__other-charges' key={key}>
                            <label className='msc-cart-line__other-charges-label'>{`${otherCharge.Description}:`}</label>
                            <span className='other-charge-value'>
                                <PriceComponent
                                    data={{
                                        price: {
                                            CustomerContextualPrice: otherCharge.CalculatedAmount
                                        }
                                    }}
                                    context={props.context}
                                    id={props.id}
                                    typeName={props.typeName}
                                    className='msc-cart-line__other-charges-actual'
                                    currencyCode={props.priceCurrency}
                                />
                            </span>
                        </div>
                    ) : (
                        ''
                    );
                })) ||
            undefined
        );
    },

    /**
     * Render the sales agreement prompt.
     * @param props - The ICartLineProps.
     * @returns The JSX.Element.
     */
    renderSalesAgreementPrompt: (props: ICartLineProps): JSX.Element | null => {
        if (props.data.cartLine.SalesAgreementLineRecordId === 0) {
            return null;
        }
        return <div className='msc-cart-line__sales-agreement-prompt'>{props.resources.salesAgreementPricePrompt}</div>;
    }
};

/**
 * Renders catalog label for the cart line.
 * @param props - Cart line props.
 * @returns Catalog label.
 */
const CatalogLabelComponent: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const catalogId = props.data.cartLine.CatalogId;
    validateCatalogId(catalogId);

    if (!isChannelTypeB2B(props.context.actionContext.requestContext) || !catalogId || !ArrayExtensions.hasElements(props.data.catalogs)) {
        return null;
    }

    const catalog = props.data.catalogs.find(item => item.RecordId === catalogId);

    if (!catalog || !catalog.Name) {
        return null;
    }

    return <div className='msc-cart-line__catalog-label'>{catalog.Name}</div>;
};

// eslint-disable-next-line no-redeclare
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {

    const { isSalesLine, productUrl, resources, backorderAvailability } = props;
    const { product, cartLine } = props.data;


    let isBackorder = false
    const availability = backorderAvailability?.find(avail => {
        return avail.ProductAvailableQuantity.ProductId === product?.RecordId;
    });
    const productAvailableQuantity = availability?.ProductAvailableQuantity.AvailableQuantity || 0

    if (
        cartLine.Quantity &&
        availability &&
        product?.ItemTypeValue !== ReleasedProductType.Service &&
        productAvailableQuantity < cartLine.Quantity
    ) {
        isBackorder = true;
    }

    // @CUSTOM: T&E customization
    const [isTestOrScore, isTestOrScoreLoaded] = React.useState(true);
    React.useEffect(() => {

        if (!isSalesLine) {

            const criteria: ProductSearchCriteria = {
                ItemIds: [{ ItemId: product?.ItemId }],
                Context: {
                    ChannelId: props.context.request.apiSettings.channelId,
                    CatalogId: getCatalogId(props.context.request)
                },
                IncludeAttributes: true
            };

            searchByCriteriaAsync({ callerContext: props.context.actionContext }, criteria).then(results => {
                const productWithAttr = results[0];
                const formattedAtr = convertProductAttributes(productWithAttr?.AttributeValues || [])
                const isTesting = !!(formattedAtr[attrNames.testType] && formattedAtr[attrNames.testType] !== attrNames.isNotTest);
                const isScoring = !!(formattedAtr[attrNames.productType] && formattedAtr[attrNames.productType] === attrNames.scoringType);

                if (isTesting || isScoring) {
                    isTestOrScoreLoaded(true);
                } else {
                    isTestOrScoreLoaded(false);
                }
            });
        }
    }, []);

    //------------------------------------------------------
    // @CUSTOM: Kit contents display
    //------------------------------------------------------
    const lineAttributes = cartLine.AttributeValues && convertCartAttributes(cartLine.AttributeValues);
    const kitContents = lineAttributes && lineAttributes[attrNames.kitContents] as string;
    const kitElectives = lineAttributes && lineAttributes[attrNames.kitElectives] as string;
    const isElective = !!(lineAttributes && lineAttributes[attrNames.kitLineParentAssociation]);

    // Do not return kit cart lines if elective
    if (isElective) {
        return null;
    }

    // Hooks for toggling Show Contents section
    const [showKitContents, toggleShowKitContents] = React.useState(false);

    // Formatted kit contents to display
    const formattedKitContents = kitContents?.split('|');
    const showKitContentsText = lineAttributes && lineAttributes.showContentsText;
    //------------------------------------------------------

    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };
    const fallbackImageUrl =
        product &&
        getFallbackImageUrl(
            product.ItemId,
            props.context.actionContext.requestContext.apiSettings,
            props.context.app?.config?.OmniChannelMedia
        );
    const imageSettings = props.imageSettings;
    imageSettings.cropFocalRegion = !ObjectExtensions.isNullOrUndefined(imageSettings);
    const { inputQuantityAriaLabel } = props.resources;
    const productDimensions = product && CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const renderDisountLines = CartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = CartLineItemFunctions.renderInventoryLabel(props);
    const renderUnitOfMeasure = CartLineItemFunctions.renderUnitOfMeasure(props);
    const renderShippingLabel = CartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = CartLineItemFunctions.renderOtherCharges(props);
    const renderSalesAgreementPrompt = CartLineItemFunctions.renderSalesAgreementPrompt(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const prodAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name || cartLine.Description;
    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-cart-line__error-message msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    const _updateQuantity = (event: React.ChangeEvent<HTMLSelectElement>) => {

        // @CUSTOM: publish events for add to cart or remove for analytics
        const newValue = Number.parseInt(event.target.value, 10)
        const quantityChange = Math.abs(newValue - (cartLine.Quantity || 0));

        const analyticsData: SingleProductEvent = {
            itemID: cartLine.ItemId!,
            name: product?.Name || '',
            discount: cartLine.DiscountAmount,
            variant: '',
            quantity: quantityChange,
            price: product?.BasePrice || cartLine.Price || 0,
            currency: props.context.request.channel?.Currency || 'USD',
            unitOfMeasure: cartLine.UnitOfMeasureSymbol
        };

        if (cartLine.Quantity && newValue > cartLine.Quantity) {

            // add to cart event
            publish('addToCart', analyticsData);

        } else if (cartLine.Quantity && newValue < cartLine.Quantity) {

            // remove from cart event
            publish('removeFromCart', analyticsData);
        }
        // end customization

        if (props.quantityOnChange) {
            props.quantityOnChange(props.data.cartLine, Number.parseInt(event.target.value, 10), props.lineIndex);
        }
    };

    const _generateMenu = (quantity: number) => {
        const nodes = [];

        for (let i = 1; i <= quantity; i++) {
            nodes.push(
                <option className='msc-cart-line__quantity__select-menu__item' value={i}>
                    {i}
                </option>
            );
        }

        return nodes;
    };

    const _generateSelectMenu = (quantity: number, currentQuantity: number | undefined): JSX.Element => {
        return (
            <select
                className='msc-cart-line__quantity__select-menu'
                aria-label={inputQuantityAriaLabel}
                value={currentQuantity}
                onChange={_updateQuantity}
            >
                {_generateMenu(quantity)}
            </select>
        );
    };

    const _generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];

        if (props.isOutOfStock) {
            return null;
        }

        // No quantity selector for invoice line
        if (!props.data.cartLine.IsInvoiceLine) {
            if (props.isQuantityEditable && !props.data.product?.IsGiftCard && !kitContents && !isTestOrScore) {        // @CUSTOM: Kit contents - only show quantity selector if product is not a kit with contents or T&E
                nodes.push(_generateSelectMenu(props.maxQuantity || 10, props.currentQuantity));
            } else {
                nodes.push(
                    <div className={classNames('msc-cart-line__quantity', { 'single-quantity': props.data.cartLine.Quantity === 1 })}>
                        <label className='quantity-label'>{resources.quantityDisplayString}</label>
                        <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                    </div>
                );
            }
        }

        //--------------------------------------------------
        // @CUSTOM: Kit contents display - price updates
        //--------------------------------------------------
        const priceData = isSalesLine ? {
            price: {
                CustomerContextualPrice: (props.data.cartLine as SalesLine).NetAmountWithAllInclusiveTax,
                BasePrice: (props.data.cartLine as SalesLine).GrossAmount
            }
        } : {
            price: {
                CustomerContextualPrice: (props.data.cartLine as CartLine).ExtendedPrice,
                BasePrice: props.data.cartLine.NetPrice
            }
        };

        // If kit contains electives, add price to kit item
        if (kitElectives) {
            const electivePrices = kitElectives.split('|').map(elective => {
                const splitData = elective.split(':');
                return Number(splitData[1]);
            });

            const electiveSum = electivePrices.reduce((accumulator, currentValue) => {
                const electivePrice = props.currentQuantity ? props.currentQuantity * currentValue : currentValue;
                return accumulator + electivePrice;
            }, 0);

            priceData.price.CustomerContextualPrice && (priceData.price.CustomerContextualPrice += electiveSum);
            priceData.price.BasePrice && (priceData.price.BasePrice += electiveSum);
        }
        //--------------------------------------------------

        nodes.push(
            <div className='msc-cart-line__product-price'>
                <PriceComponent
                    data={priceData}    // @CUSTOM: Kit contents display, replaced with conditional above
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className='discount-value'
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    salesAgreementLineId={props.data.cartLine.SalesAgreementLineRecordId}
                    currencyCode={props.priceCurrency}
                />
            </div>
        );

        return nodes;
    };

    //------------------------------------------------------
    // @CUSTOM: Getting product item ID for SKU display
    //------------------------------------------------------
    const itemId = product?.ItemId;
    const skuDisplay = itemId && (
        <div className={cls('product-sku')}>
            <span className={cls('product-sku-label')}>{labelResources.sku}</span>
            <span className={cls('product-sku-value')}>{itemId}</span>
        </div>
    );
    //------------------------------------------------------

    if (props.displayMode === 'COMPACT') {
        const reducedDimensions: string = product?.Dimensions
            ? product.Dimensions.reduce<string>((str, productDimension) => {
                if (productDimension.DimensionValue?.Value) {
                    if (str) {
                        return `${str}, ${productDimension.DimensionValue.Value}`;
                    }

                    return `${productDimension.DimensionValue.Value}`;
                }

                return str;
            }, '')
            : '';

        return (
            <div className='msc-cart-line msc-cart-line__compact'>
                <div className={imgeClassName}>
                    <Image
                        requestContext={props.context.actionContext.requestContext}
                        src={props.primaryImageUrl ?? 'empty'}
                        fallBackSrc={fallbackImageUrl}
                        altText={productName}
                        gridSettings={props.gridSettings}
                        imageSettings={imageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
                <div className='msc-cart-line__content'>
                    {MsDyn365.isBrowser ? (
                        // @CUSTOM: Check global config if links back to PDP should be disabled
                        !props.context.app.config.disableCartLineProductLinks ? (
                            <a className='msc-cart-line__product-title' {...prodAttribute} href={productUrl} key={productUrl}>
                                {productName}
                            </a>
                        ) : (
                            <span className='msc-cart-line__product-title' {...prodAttribute} key={productUrl}>
                                {productName}
                            </span>
                        )
                    ) : null}
                    {/* @CUSTOM: SKU display */}
                    {skuDisplay}
                    {reducedDimensions !== '' ? <div className='msc-cart-line__product-variants'>{reducedDimensions}</div> : ''}
                    {renderUnitOfMeasure}
                    {/* @CUSTOM: backorder customization */}
                    {isBackorder && <div className='msc-cart-line__backorder-message'>{props.context.app.config.backorderTextCartLine}</div>}
                    {_generateQuantityAndPrice()}
                    {props.data.cartLine.IsInvoiceLine && props.data.cartState && props.resources.priceEditorResources && (
                        <CartLinePriceEditor
                            className='msc-cart-line__price-editor-container'
                            context={props.context}
                            resources={props.resources.priceEditorResources}
                            cartState={props.data.cartState}
                            cartLine={props.data.cartLine}
                        />
                    )}
                </div>
                {_generateErrorMessage()}
            </div>
        );
    }

    return (
        <>
            <div className='msc-cart-line'>
                <div className={imgeClassName}>
                    <Image
                        requestContext={props.context.actionContext.requestContext}
                        src={props.primaryImageUrl ?? ''}
                        fallBackSrc={fallbackImageUrl}
                        altText={productName}
                        gridSettings={props.gridSettings}
                        imageSettings={imageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
                <div className='msc-cart-line__content'>
                    <div className='msc-cart-line__product'>
                        <CatalogLabelComponent {...props} />
                        {MsDyn365.isBrowser ? (
                            // @CUSTOM: Check global config if links back to PDP should be disabled
                            !props.context.app.config.disableCartLineProductLinks ? (
                                <a className='msc-cart-line__product-title' {...prodAttribute} href={productUrl} key={productUrl}>
                                    {productName}
                                </a>
                            ) : (
                                <span className='msc-cart-line__product-title' {...prodAttribute} key={productUrl}>
                                    {productName}
                                </span>
                            )
                        ) : null}
                        {/* @CUSTOM: SKU display */}
                        {skuDisplay}
                        {ArrayExtensions.hasElements(productDimensions) ? (
                            <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                        ) : (
                            ''
                        )}
                        {renderUnitOfMeasure}
                        {renderDisountLines}
                        {renderInventoryLabel}
                        {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                        {renderOtherCharges}
                        {renderSalesAgreementPrompt}
                        {/* @CUSTOM: backorder customization */}
                        {isBackorder && <div className='msc-cart-line__backorder-message'>{props.context.app.config.backorderTextCartLine}</div>}
                    </div>
                    {_generateQuantityAndPrice()}
                    {props.data.cartLine.IsInvoiceLine && props.data.cartState && props.resources.priceEditorResources && (
                        <CartLinePriceEditor
                            className='msc-cart-line__price-editor-container'
                            context={props.context}
                            resources={props.resources.priceEditorResources}
                            cartState={props.data.cartState}
                            cartLine={props.data.cartLine}
                        />
                    )}
                </div>
                {/* @CUSTOM: Kit contents display */}
                {formattedKitContents && (
                    <div className='msc-cart-line__kit-contents'>
                        <a className='msc-cart-line__kit-contents-button' role='button' aria-expanded={showKitContents} onClick={() => toggleShowKitContents(!showKitContents)}>
                            {showKitContentsText}
                        </a>
                        <div className='msc-cart-line__kit-contents-items' aria-hidden={!showKitContents}>
                            {
                                formattedKitContents.map((item, index) => {
                                    const isCustomized = item.includes('*');
                                    let productName = item;
                                    if (isCustomized) {
                                        productName = item.replace('*', '');
                                    }

                                    return (
                                        <div key={index} className={classNames('msc-cart-line__kit-contents-item', { 'customized': isCustomized })}>
                                            {productName}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )}
                {/* @CUSTOM: End kit contents display customization */}
            </div>
            {_generateErrorMessage()}
        </>
    );
};

// @ts-expect-error
export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponentOverride<ICartline>('CartLineItem', {
    component: CartLine,
    ...CartLineItemFunctions
});


export default CartLineItemComponent;